import { useAsyncData } from 'nuxt/app'
import { computed, ref, watch, type Ref } from 'vue'
import { useAuthStore } from '~/stores/authStore'
import { useSecurityStore } from '~/stores/securityStore'
import { useUserStore } from '~/stores/userStore'
import { URL_CONFIG } from '~~/shared/utils/url.const'

export const AGENTIC_FILTER_INIT_VARIANT = 'control'


export interface AgenticFilterInitResponse {
  filter_id: string
  filter_state: string
  content: {
    title: string
    short_description: string
  }
  facet_filters: {
    filter_name: string
    display_name: string
  }[]
}

type JsonValue = string | number | boolean | null | JsonValue[] | { [key: string]: JsonValue }

interface AgenticFilterInitErrorResponse {
  detail?: JsonValue
  [key: string]: JsonValue | undefined
}

type AgenticFilterInitApiResponse = AgenticFilterInitResponse | AgenticFilterInitErrorResponse

const isAgenticFilterInitResponse = (
  response: AgenticFilterInitApiResponse
): response is AgenticFilterInitResponse =>
  'filter_id' in response && typeof response.filter_id === 'string'

interface UseAgenticFilterInitOptions {
  selectedFilters: Ref<Record<string, string[]>>
  siteCategory: Ref<string | null>
  siteSortOption: Ref<string>
  shouldInitFilter?: Ref<boolean>
}

const HEALTH_AND_INGREDIENT_KEYS = ['health_needs', 'ingredients'] as const
const MAX_COMBINED_HEALTH_AND_INGREDIENTS = 2
const limitHealthIngredientFilters = ref(false)

type HealthAndIngredientKey = (typeof HEALTH_AND_INGREDIENT_KEYS)[number]

type HealthIngredientEntry = {
  key: HealthAndIngredientKey
  value: string
}

export const normalizeAgenticFilterName = (name: string) =>
  name
    .trim()
    .replace(/&/g, 'and')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .toLowerCase()

export const buildAgenticSiteFilters = (selectedFilters: Record<string, string[]>) => {
  return Object.entries(selectedFilters).reduce<Record<string, string[]>>((out, [name, values]) => {
    const filterName = normalizeAgenticFilterName(name)
    const filterValues = values.filter(Boolean)

    if (filterName && filterValues.length > 0) {
      out[filterName] = filterValues
    }

    return out
  }, {})
}

const getHealthIngredientEntries = (filters: Record<string, string[]>): HealthIngredientEntry[] => {
  return HEALTH_AND_INGREDIENT_KEYS.flatMap((key) => {
    return (filters[key] ?? []).map((value) => ({ key, value }))
  })
}

const upsertEntry = (entries: HealthIngredientEntry[], entry: HealthIngredientEntry) => {
  if (entries.some((item) => item.key === entry.key && item.value === entry.value)) {
    return
  }

  entries.push(entry)
}

export const limitHealthNeedsAndIngredients = (
  normalizedFilters: Record<string, string[]>,
  previousFilters: Record<string, string[]>
) => {
  const nextFilters = { ...normalizedFilters }
  const currentEntries = getHealthIngredientEntries(nextFilters)

  if (currentEntries.length <= MAX_COMBINED_HEALTH_AND_INGREDIENTS) {
    return nextFilters
  }

  const currentSet = new Set(currentEntries.map((entry) => `${entry.key}:${entry.value}`))
  const previousEntries = getHealthIngredientEntries(previousFilters).filter((entry) =>
    currentSet.has(`${entry.key}:${entry.value}`)
  )

  const retainedEntries: HealthIngredientEntry[] = []

  previousEntries.forEach((entry) => {
    if (retainedEntries.length < MAX_COMBINED_HEALTH_AND_INGREDIENTS) {
      upsertEntry(retainedEntries, entry)
    }
  })

  currentEntries.forEach((entry) => {
    if (retainedEntries.length < MAX_COMBINED_HEALTH_AND_INGREDIENTS) {
      upsertEntry(retainedEntries, entry)
    }
  })

  HEALTH_AND_INGREDIENT_KEYS.forEach((key) => {
    const values = retainedEntries
      .filter((entry) => entry.key === key)
      .map((entry) => entry.value)

    if (values.length > 0) {
      nextFilters[key] = values
    } else {
      delete nextFilters[key]
    }
  })

  return nextFilters
}

const applyHealthIngredientFilter = (
  normalizedFilters: Record<string, string[]>,
  previousFilters: Record<string, string[]>
) => {
  return limitHealthIngredientFilters.value
    ? limitHealthNeedsAndIngredients(normalizedFilters, previousFilters)
    : normalizedFilters
}

export const hasAgenticFilterInitContext = (
  siteFilters: Record<string, string[]>,
  _siteCategory: string | null
) => Object.keys(siteFilters).length > 0


export const useAgenticFilterInit = ({
  selectedFilters,
  siteCategory,
  siteSortOption,
  shouldInitFilter: shouldInitFilterOverride
}: UseAgenticFilterInitOptions) => {
  const authStore = useAuthStore()
  const config = useRuntimeConfig()
  const BASE_URL = config.public.tgptBaseUrl
  const securityStore = useSecurityStore()
  const userStore = useUserStore()
  const inflightRequestKey = ref<string | null>(null)
  const cachedResponse = ref<AgenticFilterInitResponse | null>(null)
  let requestGeneration = 0

  const customerId = computed(() => userStore.data?.customerId != null ? String(userStore.data.customerId) : null)
  const normalizedFilters = buildAgenticSiteFilters(selectedFilters.value)
  const siteFilters = ref<Record<string, string[]>>(
    applyHealthIngredientFilter(normalizedFilters, {})
  )
  const shouldInitFilter = computed(() =>
    shouldInitFilterOverride?.value ?? hasAgenticFilterInitContext(siteFilters.value, siteCategory.value)
  )

  const buildRequestBody = (
    filters: Record<string, string[]>,
    category: string | null,
    sortOption: string
  ) => ({
    session_id: authStore.sessionId,
    customer_id: customerId.value,
    site_filters: filters,
    site_category: category === 'Supplements' ? '' : category,
    site_sort_option: sortOption,
    variant: AGENTIC_FILTER_INIT_VARIANT
  })

  const buildRequestHeaders = async () => {
    if (!securityStore.data?.csrf) {
      await securityStore.fetch()
    }

    const csrf = securityStore.data?.csrf

    if (!csrf) {
      throw new Error('Invalid CSRF token/data.')
    }

    return {
      Authorization: `Bearer ${authStore.token}`,
      'Session-Id': authStore.sessionId,
      [csrf.headerName]: csrf.token
    }
  }

  const fetchAgenticFilterInit = async () => {
    if (!shouldInitFilter.value) {
      cachedResponse.value = null
      return null
    }

    const filters = Object.fromEntries(
      Object.entries(siteFilters.value).map(([name, values]) => [name, [...values]])
    )
    const category = siteCategory.value
    const sortOption = siteSortOption.value

    await authStore.ensureSession()
    const requestBody = buildRequestBody(filters, category, sortOption)
    const requestKey = JSON.stringify(requestBody)

    if (inflightRequestKey.value === requestKey) {
      return cachedResponse.value
    }

    const generation = ++requestGeneration
    inflightRequestKey.value = requestKey

    try {
      const response = await useNuxtApp().$siteFetch<AgenticFilterInitApiResponse>(
        `${BASE_URL}${URL_CONFIG.AGENTIC_FILTER_INIT_ENDPOINT}`,
        {
          method: 'POST',
          body: requestBody,
          headers: await buildRequestHeaders(),
          credentials: 'include'
        }
      )

      if (generation !== requestGeneration) return cachedResponse.value

      if (!isAgenticFilterInitResponse(response)) {
        cachedResponse.value = null
        return null
      }

      cachedResponse.value = response
      return response
    } catch (requestError) {
      if (generation !== requestGeneration) return cachedResponse.value

      cachedResponse.value = null
      throw requestError
    } finally {
      if (inflightRequestKey.value === requestKey) {
        inflightRequestKey.value = null
      }
    }
  }

  const { data, pending, error, refresh } = useAsyncData<AgenticFilterInitResponse | null>(
    `agentic-filter-init-${Date.now()}`,
    fetchAgenticFilterInit,
    {
      server: true,
      lazy: false,
      immediate: false,
      default: () => null,
      watch: []
    }
  )

  const refreshAgenticFilter = async () => {
    cachedResponse.value = null
    data.value = null
    await refresh({ dedupe: 'cancel' })
    return data.value
  }

  const initFilter = async () => {
    return refreshAgenticFilter()
  }

  watch(
    () => JSON.stringify({
      selectedFilters: selectedFilters.value,
      siteCategory: siteCategory.value,
      siteSortOption: siteSortOption.value,
      shouldInitFilter: shouldInitFilterOverride?.value
    }),
    () => {
      const normalizedFilters = buildAgenticSiteFilters(selectedFilters.value)
      siteFilters.value = applyHealthIngredientFilter(
        normalizedFilters,
        siteFilters.value
      )

      if (!shouldInitFilter.value) return

      void refreshAgenticFilter()
    },
    { immediate: true, flush: 'post' }
  )

  return {
    data,
    pending,
    error,
    siteFilters,
    shouldInitFilter,
    initFilter,
    refresh: refreshAgenticFilter
  }
}